/**
 * GET /admin/me returns `permissions` as a module → letters map, e.g.
 * { "orders": "ru", "dashboard": "r" } (docs/frontend/README.md §Admin: permissions).
 * The backend checks every call anyway; this only decides what the UI shows.
 */
export const ADMIN_MODULES = [
  'dashboard',
  'orders',
  'products',
  'categories',
  'customers',
  'stores',
  'admins',
  'roles',
  'integrations',
  'settings',
  'audit',
  'translations',
] as const

export type AdminModule = (typeof ADMIN_MODULES)[number]

/** r = read (menu item), c = create, u = update, d = delete. */
export type PermissionLetter = 'r' | 'c' | 'u' | 'd'

export type PermissionMap = Readonly<Record<string, string>>

/**
 * Отзывы покупателей видит только роль «Супер-админ» (role_id 1) — так решил
 * заказчик; права в сетке ролей у них нет, дать их другой роли нельзя.
 * Сервер проверяет то же самое.
 */
export const REVIEWS_ROLE_ID = 1

export function canSeeReviews(roleId: number): boolean {
  return roleId === REVIEWS_ROLE_ID
}

/** Модули новых функций, ручки которых пока mock. */
export interface MockedModules {
  stories: boolean
  news: boolean
}

/**
 * Модуль, чьи права проверять. Сторис и новости пока живут на mock: сервер
 * модулей `stories` и `news` не знает, и ни у одной роли их нет. До тех пор
 * права берутся у «Категорий» — витрину ведут одни и те же люди
 * (контент-менеджер crud, маркетолог ru, остальные только смотрят). С
 * настоящим API у каждой функции свой модуль.
 */
export function permissionModule(module: string, mocked: MockedModules): string {
  // Уведомления — про заказы: сервер открывает колокольчик, push и живые
  // события тем, у кого есть `orders:r` (R-117), своего модуля у них нет.
  if (module === 'notifications') return 'orders'
  return (module === 'stories' && mocked.stories) || (module === 'news' && mocked.news)
    ? 'categories'
    : module
}

export function can(
  permissions: PermissionMap,
  module: string,
  letter: PermissionLetter = 'r',
): boolean {
  return permissions[module]?.includes(letter) ?? false
}

/** Modules the staff member may open, in menu order. */
export function visibleModules(permissions: PermissionMap): AdminModule[] {
  return ADMIN_MODULES.filter((module) => can(permissions, module))
}

/** null store_id means the whole network — then the pharmacy filter is hidden. */
export function isNetworkWide(storeId: number | null): boolean {
  return storeId === null
}
